import baseToast, { Renderable, ToastOptions } from 'react-hot-toast';

/**
 * THE APP'S ONE TOAST ENTRY POINT.
 *
 * ── WHAT WAS WRONG ──
 * Every call site imported react-hot-toast directly, and react-hot-toast mints a
 * FRESH ID per call. So pressing Sign In three times with the wrong password
 * produced three identical "Invalid email or password" bars, stacked, each
 * holding for the error duration of eight seconds — the reported "it reappears
 * and stacks" and "the previous message isn't disappearing". Nothing was firing
 * twice; the same message simply had no identity, so nothing could replace it.
 *
 * ── WHAT THIS DOES ──
 *   1. IDENTITY FROM THE MESSAGE. The same text lands on the same toast id, so a
 *      repeat REPLACES the bar (and restarts its timer) instead of adding one.
 *      That is react-hot-toast's own documented behaviour for a repeated id.
 *   2. A CEILING. Three bars at once, oldest dismissed first. A screen behind
 *      six stacked toasts is a screen you cannot read, and a burst of failures
 *      (a save that fans out into several requests) produced exactly that.
 *   3. ONE PLACE TO CHANGE ANY OF IT. Durations and placement live in App.tsx's
 *      <Toaster>; identity lives here.
 *
 * A caller that genuinely wants two bars of the same text — nobody does today —
 * passes its own `id`, which wins.
 */

// Only strings can carry identity. A custom node (JSX) is passed straight
// through with react-hot-toast's own id, because two different nodes could
// stringify the same and collapsing them would hide a real message.
const idFor = (message: Renderable): string | undefined =>
  typeof message === 'string' ? `lb:${message.slice(0, 120)}` : undefined;

// Live ids, oldest first. Cheap and exact: every mint goes through here.
const live: string[] = [];
const MAX_VISIBLE = 3;

const remember = (id: string): void => {
  const at = live.indexOf(id);
  if (at !== -1) live.splice(at, 1);
  live.push(id);
  while (live.length > MAX_VISIBLE) {
    const oldest = live.shift();
    if (oldest) baseToast.dismiss(oldest);
  }
};

const withIdentity = (
  message: Renderable,
  options?: ToastOptions
): ToastOptions | undefined => {
  const id = options?.id || idFor(message);
  if (!id) return options;
  remember(id);

  return { ...options, id };
};

type ToastFn = (message: Renderable, options?: ToastOptions) => string;

const wrap =
  (fn: ToastFn): ToastFn =>
  (message, options) =>
    fn(message, withIdentity(message, options));

/**
 * Drop-in for react-hot-toast's default export: `toast(...)`, `toast.success`,
 * `toast.error`, plus the pass-throughs the app uses. Call sites keep reading
 * exactly as they did.
 */
export const toast = Object.assign(wrap(baseToast as unknown as ToastFn), {
  success: wrap(baseToast.success),
  error: wrap(baseToast.error),
  loading: wrap(baseToast.loading),
  custom: baseToast.custom,
  dismiss: baseToast.dismiss,
  remove: baseToast.remove,
  promise: baseToast.promise,
});

export default toast;
